/**
	* Event system class
	* 
	* @remarks
	* Events can be set and called globally via _events (as defined in config/global)
	* 
	* @param events		-	 Map containing the event names and handlers
*/
export default class EventSystem {
	private events:Map<string, Function[]> = new Map<string, Function[]>();
	private singleEvents:Map<string, Function[]> = new Map<string, Function[]>();
	
	/**
		* Adds an event listener
		* 
		* @example
		* `_events.on("resize", (dimensions:any) => { console.log(dimensions) })`
		* 
		* @param event		-	Event name
		* @param handler	-	Method to be called
	*/
	on(event:string, handler:Function) {
		let handlers:Function[] = this.events.get(event) || [];
		
		handlers.push(handler);
		
		this.events.set(event, handlers);
	}
	
	/**
		* Adds an event listener (one off)
		* 
		* @example
		* `_events.once("resize", (dimensions:any) => { console.log(dimensions) })`
		* 
		* @param event		-	Event name
		* @param handler	-	Method to be called
	*/
	once(event:string, handler:Function) {
		let handlers:Function[] = this.singleEvents.get(event) || [];
		
		handlers.push(handler);
		
		this.singleEvents.set(event, handlers);
	}
	
	/**
		* Removes an event listener
		* 
		* @param event - Event name
		* @param handler - Method to be removed
	*/
	un(event:string, handler:Function) {
		let handlers:Function[] = this.events.get(event) || [];
		
		handlers = handlers.filter(item => item !== handler);
		
		this.events.set(event, handlers);
	}
	
	/**
		* Removes all event listeners for event
		* 
		* @param event - Event name
	*/
	unall(event:string) {
		let handlers:Function[] = this.events.get(event) || [];
		
		handlers = [];
		
		this.events.set(event, handlers);
	}
	
	/**
		* Invokes an event listener
		*
		* @remarks
		* Does nothing if no such event listener is found
		*
		* @example
		* `_events.invoke("resize", {width: window.innerWidth, height: window.innerHeight})`
		* 
		* @param event		-	Event name
		* @param ...params	-	Parameters passed to handling method
	*/
	invoke(event:string, ...params:any) {
		this.events.forEach((handlers:Function[], key:string) => {
			if(key != event) return;
			
			handlers.forEach((handler:Function) => {
				handler(...params);
			})
		});
		this.singleEvents.forEach((handlers:Function[], key:string) => {
			if(key != event) return;
			
			handlers.forEach((handler:Function) => {
				handler(...params);
			});
			
			this.singleEvents.set(event, []);
		});
	}
}